/**
 * @file     ResultHero.tsx
 * @purpose  The live result card every family 7 tool shares: an "Estimate"
 *           label, the figure, one sentence on what it means, the next
 *           actions, and the one-line estimate disclaimer.
 */
'use client';

import Box from '@mui/material/Box';
import { eyebrowSx, hairline, headingFont, hubColors, hubRadius, microLabelSx } from '@/components/hub/hubKitStyles';

type ResultHeroProps = {
  id?: string;
  eyebrow: string;
  label: string;
  /** Null renders the empty state line instead of a figure. */
  value: string | null;
  emptyText?: string;
  meaning?: React.ReactNode;
  actions?: React.ReactNode;
  disclaimer?: React.ReactNode;
  children?: React.ReactNode;
};

export function ResultHero({ id, eyebrow, label, value, emptyText, meaning, actions, disclaimer, children }: ResultHeroProps) {
  return (
    <Box
      id={id}
      component="section"
      aria-label={label}
      sx={{
        border: hairline,
        borderRadius: `${hubRadius.panel}px`,
        backgroundColor: hubColors.surfaceCard,
        p: { xs: 4, sm: 6 },
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        scrollMarginTop: 96,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box component="p" sx={eyebrowSx}>{eyebrow}</Box>
        <Box component="p" sx={{ ...microLabelSx, color: hubColors.textSecondary }}>{label}</Box>
        {value ? (
          <Box
            component="p"
            sx={{
              m: 0,
              fontFamily: headingFont,
              fontSize: 'clamp(40px, 6cqi, 60px)',
              fontWeight: 600,
              lineHeight: 1.02,
              letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
              color: hubColors.textPrimary,
              overflowWrap: 'anywhere',
            }}
          >
            {value}
          </Box>
        ) : (
          <Box component="p" sx={{ m: 0, fontSize: 17, lineHeight: 1.5, color: hubColors.textSecondary }}>
            {emptyText}
          </Box>
        )}
        {meaning ? (
          <Box component="p" sx={{ m: 0, fontSize: 15, lineHeight: 1.55, color: hubColors.textSecondary, textWrap: 'pretty' }}>
            {meaning}
          </Box>
        ) : null}
      </Box>

      {actions ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, '& > *': { width: '100%' } }}>{actions}</Box>
      ) : null}

      {children}

      {disclaimer ? (
        <Box component="p" sx={{ m: 0, pt: 2, borderTop: hairline, fontSize: 12.5, lineHeight: 1.55, color: hubColors.textMuted }}>
          {disclaimer}
        </Box>
      ) : null}
    </Box>
  );
}
